import human from 'humanparser';
import { isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

const humanParserConfig = { ignoreSuffix: ['ma'] };

export function formatPhone(phone: string, format: 'Pretty' | 'E164' = 'Pretty'): string {
  const number = parsePhoneNumber(phone, 'US');
  switch (format) {
    case 'Pretty':
      return number.formatNational().toString();
    case 'E164':
      return number.number.toString();
  }
}

export function isPhoneNumber(phone?: string): boolean {
  return phone ? isPossiblePhoneNumber(phone, 'US') : false;
}

export function getFirstName(name: string): string {
  const parsedName = human.parseName(name, humanParserConfig);
  return parsedName?.lastName ? (parsedName?.firstName as string) : '';
}

export function getLastName(name: string): string {
  const parsedName = human.parseName(name, humanParserConfig);
  return (parsedName?.lastName as string) || parsedName.fullName;
}

export function getFirstNameLastInitial(name: string): string {
  return `${getFirstName(name)} ${getLastName(name).substring(0, 1)}.`.trim();
}

export function sanitizeFullName(fullName: string): string {
  // biome-ignore lint/complexity/noMultipleSpacesInRegularExpressionLiterals: build fails with suggestion
  return fullName.replace(/  +/g, ' ').replaceAll('’', `'`);
}

/**
 * Pattern for validating full names.
 * Allows:
 * - Letters (a-z, A-Z, and accented characters À-ÿ) - must start with at least one letter
 * - Hyphens (for names like "Mary-Jane")
 * - Apostrophes (for names like "O'Brien")
 * - Ampersands (for names like "Smith & Jones")
 * - Numbers (for suffixes like "III" or "2nd")
 * - Spaces (between name parts)
 */
export const FULL_NAME_PATTERN_STRICT = /^[a-zA-ZÀ-ÿ]+[-a-zA-ZÀ-ÿ0-9'& ]*$/u;

/**
 * Same as FULL_NAME_PATTERN_STRICT, but also allows the name to start with a
 * number (e.g. a landlord entering an address, like "123 Main Street").
 * Gated behind the `nameAllowLeadingNumber` PostHog feature flag in consuming apps.
 */
export const FULL_NAME_PATTERN_LENIENT = /^[a-zA-ZÀ-ÿ0-9]+[-a-zA-ZÀ-ÿ0-9''& ]*$/u;

/**
 * Maximum length allowed when validating a full name with isValidFullName.
 * Guards against ReDoS: FULL_NAME_PATTERN_STRICT/_LENIENT contain adjacent
 * quantified character classes that overlap (e.g. letters appear in both the
 * leading `+` and trailing `*` groups), which can cause catastrophic
 * backtracking on pathological input (e.g. a long run of the same
 * character). Real full names/addresses are always far shorter than this.
 */
export const MAX_FULL_NAME_LENGTH = 256;

/**
 * Validates a full name against FULL_NAME_PATTERN_STRICT or, when
 * `allowLeadingNumber` is true, the more permissive FULL_NAME_PATTERN_LENIENT.
 * Rejects values longer than MAX_FULL_NAME_LENGTH before running the regex.
 */
export function isValidFullName(
  value: string,
  options?: { allowLeadingNumber?: boolean },
): boolean {
  if (value.length > MAX_FULL_NAME_LENGTH) return false;
  const pattern = options?.allowLeadingNumber
    ? FULL_NAME_PATTERN_LENIENT
    : FULL_NAME_PATTERN_STRICT;
  return pattern.test(value);
}

export function camelCaseToWords(s: string): string {
  const result = s.replace(/([A-Z])/g, ' $1');
  return (result.charAt(0).toUpperCase() + result.slice(1)).trim();
}

export function snakeCaseToWords(s: string): string {
  return s
    .split('_')
    .filter((x) => x.length > 0)
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
    .join(' ');
}

export function snakeToCamelCase(name: string): string {
  return name.replace(/([-_][a-z])/gi, (char) => char.toUpperCase().replace('_', ''));
}

export function formatCustomKey(s: string): string {
  if (s.includes('_')) return snakeCaseToWords(s);
  return camelCaseToWords(s);
}

export function isPossibleDate(s: string): boolean {
  const timestamp = Date.parse(s);
  return !Number.isNaN(timestamp);
}

export function formatCustomValue(s: string): number | string {
  if (isPossibleDate(s)) return Date.parse(s);
  return s;
}

// biome-ignore lint/suspicious/noExplicitAny: transformer function should handle type issues
type AcceptableAny = any;
export function transformObjectValues<T extends Record<string, AcceptableAny>>(
  input: Record<string, AcceptableAny>,
  transformer: (value: AcceptableAny) => AcceptableAny,
  keys?: string[],
) {
  return Object.fromEntries(
    Object.entries(input).map(([key, value]) => {
      if (keys) return keys?.includes(key) ? [key, transformer(value)] : [key, value];
      return [key, transformer(value)];
    }),
  ) as T;
}

/**
 * Recursively transforms object keys at all levels using the provided transformer function
 * Handles nested objects and arrays
 *
 * @param input - The input value to transform (object, array, or primitive)
 * @param transformer - Function to transform each key
 * @returns The transformed value with transformed keys
 */
export function transformObjectKeysRecursive<T>(
  input: unknown,
  transformer: (key: string) => string,
): T {
  // Handle null or undefined
  if (input == null) {
    return input as T;
  }

  // Handle primitive values (string, number, boolean)
  if (typeof input !== 'object') {
    return input as T;
  }

  // Handle arrays - recursively transform each element
  if (Array.isArray(input)) {
    return input.map((item) => transformObjectKeysRecursive(item, transformer)) as T;
  }

  // Handle objects - transform keys and recursively transform values
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    const transformedKey = transformer(key);
    result[transformedKey] = transformObjectKeysRecursive(value, transformer);
  }

  return result as T;
}

export function transformSnakeToCamel<T>(input: unknown): T {
  return transformObjectKeysRecursive<T>(input, snakeToCamelCase);
}
